import React, { useState, useRef, useEffect } from "react";
import {
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  Box,
  List,
  ListItem,
  Text,
  Flex,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import routes from "routes";

export function SearchBar(props) {
  const { variant, background, children, placeholder, borderRadius, ...rest } = props;

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Chakra Color Mode
  const searchIconColor = useColorModeValue("gray.700", "white");
  const inputBg = useColorModeValue("secondaryGray.300", "navy.900");
  const inputText = useColorModeValue("gray.700", "gray.100");
  const dropdownBg = useColorModeValue("white", "navy.800");
  const dropdownHoverBg = useColorModeValue("secondaryGray.100", "navy.700");
  const dropdownBorder = useColorModeValue("gray.200", "navy.600");

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle search input change
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim() === "") {
      setFilteredRoutes([]);
      setIsOpen(false);
      setSelectedIndex(-1);
      return;
    }

    // Filter routes based on search term
    const filtered = routes.filter((route) =>
      route.name.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredRoutes(filtered);
    setIsOpen(filtered.length > 0);
    setSelectedIndex(-1);
  };

  // Handle route selection
  const handleRouteSelect = (route) => {
    navigate(route.layout + route.path);
    setSearchTerm("");
    setFilteredRoutes([]);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || filteredRoutes.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredRoutes.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredRoutes.length) {
          handleRouteSelect(filteredRoutes[selectedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <Box position="relative" ref={searchRef} w={{ base: "100%", md: "200px" }} {...rest}>
      <InputGroup>
        <InputLeftElement
          children={
            <IconButton
              bg="inherit"
              borderRadius="inherit"
              _hover="none"
              _active={{
                bg: "inherit",
                transform: "none",
                borderColor: "transparent",
              }}
              _focus={{
                boxShadow: "none",
              }}
              icon={<SearchIcon color={searchIconColor} w="15px" h="15px" />}
            />
          }
        />
        <Input
          variant="search"
          fontSize="sm"
          bg={background ? background : inputBg}
          color={inputText}
          value={searchTerm}
          onChange={handleSearch}
          onKeyDown={handleKeyDown}
          fontWeight="500"
          _placeholder={{ color: "gray.400", fontSize: "14px" }}
          borderRadius={borderRadius ? borderRadius : "30px"}
          placeholder={placeholder ? placeholder : "Search..."}
        />
      </InputGroup>

      {/* Dropdown Results */}
      {isOpen && filteredRoutes.length > 0 && (
        <Box
          position="absolute"
          top="calc(100% + 8px)"
          left="0"
          right="0"
          bg={dropdownBg}
          borderRadius="12px"
          border="1px solid"
          borderColor={dropdownBorder}
          boxShadow="lg"
          zIndex="1000"
          maxH="300px"
          overflowY="auto"
        >
          <List spacing={0}>
            {filteredRoutes.map((route, index) => (
              <ListItem
                key={route.path}
                px="16px"
                py="12px"
                cursor="pointer"
                bg={selectedIndex === index ? dropdownHoverBg : "transparent"}
                _hover={{ bg: dropdownHoverBg }}
                onClick={() => handleRouteSelect(route)}
                borderBottom={
                  index < filteredRoutes.length - 1 ? "1px solid" : "none"
                }
                borderColor={dropdownBorder}
              >
                <Flex align="center" gap="12px">
                  <Box color={searchIconColor}>{route.icon}</Box>
                  <Box>
                    <Text fontWeight="500" fontSize="sm" color={inputText}>
                      {route.name}
                    </Text>
                    {/* <Text fontSize="xs" color="gray.500">
                      {route.layout + route.path}
                    </Text> */}
                  </Box>
                </Flex>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
}